import { Outlet } from "react-router-dom";
import { PageMasterMode, usePageContext } from "../../../../../components/navigation/PageContext";

import {
  compareMeetingSessionRoles,
  isSpectator,
  MeetingSessionOvenMediaReceiver,
  useMeetingAudioMixer,
  useMeetingSelector
} from "@kalyzee/kast-web-sdk";
import {
  AudioMixerOptions,
  awaitWithMinimumDelay,
  MediaCaptureRef,
  useRefAutoUpdate
} from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSessionHighlightingState, MeetingState, MeetingUpdateByIdResult } from "@kalyzee/kast-web-sdk/websocket";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError, toastSuccess } from "../../../../../helpers/toast";
import { MeetingSourceExtended, useMeetingDataOnChange, useMeetingSources } from "../../../../../hooks/meeting";

import { DEFAULT_AUDIO_MATCH } from "../../../../../helpers/defaultMatchers";
import { matchEngine } from "../../../../../helpers/matchEngine";
import { isExpertVideoSession } from "../../../../../helpers/meeting";

import PauseView from "../../../../../components/utils/PauseView";
import { useSettings } from "../../../../../hooks/settings";

import { BottomPanelWebrtcSessionStats } from "../../../../../components/debug/BottonPanelWebrtcSessionStats";
import { DevSoundIndicatorsBanner } from "../../../../../components/utils/DevSoundIndicatorBanner";

import { BoxOverlay, BoxOverlayProps, OverlayContainer, Button, PrimaryButton, ReactIcons, RevealContainer } from "@kalyzee/kalyzee-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MasterUI } from "../../../../../components/common/MasterUI";
import { MediaSourcePicker } from "../../../../../components/common/MediaSourcePicker";
import { MediaSourceWorkBox } from "../../../../../components/workBoxes/MediaSourceWorkBox";
import { ShareWorkBox } from "../../../../../components/workBoxes/ShareWorkBox";
import { SessionMode } from "../../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../../contexts/appStore/selectors";
import { getAudioContextForAudioMixer } from "../../../../../helpers/audio";
import { addPopup, removePopup, removePopupByGroup } from "../../../../../helpers/popup";
import { isExpert } from "../../../../../helpers/roles";

import clsx from "clsx";
import { LoadingView } from "../../../../../components/utils/LoadingView";
import { useAppStoreContext, useAppStoreDispatch } from "../../../../../contexts/appStore/context";
import { useMeetingUtilsContext } from "../../../../../contexts/meetingUtils/context";
import { useBoardMedia } from "../../../../../hooks/board";
import { useMeetingMasterMessagingBridge } from "../../../../../hooks/secondaryScreens/messagingBridge";

import { BottomBar } from "../../../../../components/common/BottomBar";
import { CurrentWorkBox } from "../../../../../components/common/CurrentWorkBox";
import { HighlightPopup } from "../../../../../components/common/HighlightPopup";
import { SideBar } from "../../../../../components/common/SideBar";
import { SurveyBar } from "../../../../../components/common/SurveyBar";
import { useSdkContext } from "../../../../../contexts/sdk/context";
import { useFeatures } from "../../../../../hooks/features";
import { useHighlightNotifications } from "../../../../../hooks/highlight";
import { LayoutType } from "../../../../../interfaces/ui";
import styles from "./base.module.css";

export const ConnectedMeetingPage = () => {
  const [settings] = useSettings();

  const features = useFeatures();

  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const sdkCtx = useSdkContext();

  const selfUser = useAppStoreSelector(state => state.user);
  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const outputAudioDeviceInfo = useAppStoreSelector(state => state.outputAudioDeviceInfo);
  const meetingOptions = useAppStoreSelector(state => state.meetingOptions);
  const workBoxOpen = useAppStoreSelector(state => state.workBoxOpen);

  const dispatch = useAppStoreDispatch();
  const appStoreCtx = useAppStoreContext();

  const { containerAPI, populated } = useMeetingUtilsContext();
  const containerAPIRef = useRefAutoUpdate(containerAPI);

  const [showEndMeetingConfirmationPopup, setShowEndMeetingConfirmationPopup] = useState(false);

  const [meeting] = useMeetingSelector(containerAPI, "meeting");
  const [session] = useMeetingSelector(containerAPI, "session");
  const [sessions] = useMeetingSelector(containerAPI, "sessions")
  const [rooms] = useMeetingSelector(containerAPI, "rooms");
  const [emissionSessions] = useMeetingSelector(containerAPI, "emissionSessions");
  const [receptionSessions] = useMeetingSelector(containerAPI, "receptionSessions");
  const [chatrooms] = useMeetingSelector(containerAPI, "chatrooms");

  const mediaSelectorRef = useRef<MediaCaptureRef>();

  const [chatAdapters] = useMeetingSelector(containerAPI, "chatAdapters");
  const chatInfo = useMemo(() => {
    const room = chatrooms?.[0];
    if (!room) return undefined;
    const adapter = chatAdapters?.[room.id];
    return { room, adapter };
  }, [chatrooms, chatAdapters]);

  const [unreadMessagesNb, setUnreadMessagesNb] = useState(0);
  useEffect(() => {
    const handle = chatInfo?.adapter?.addEventListener("messages", (messages) => {
      setUnreadMessagesNb(messages.filter(m => !m.read).length);
    });
    return () => handle?.flush();
  }, [chatInfo?.adapter]);

  const isStoppingRef = useRef(false);

  const sources = useMeetingSources();

  // When the previous page navigated here with `state.openShare`
  // (e.g. CreateMeetingPopup after creating + joining), auto-open the
  // share popup so the creator can immediately share the link. We
  // consume the state by replacing the location with `state: null`,
  // which prevents re-triggering on refresh.
  useEffect(() => {
    const ctx = pageContextRef.current;
    const state = ctx?.location?.state as { openShare?: boolean } | null | undefined;
    if (!state?.openShare) return;
    dispatch("setPopupMode", "share");
    window.history.replaceState(null, "", window.location.href);
  }, [dispatch]);

  // Handles all the notifications
  // and the media selection popup for highlighting
  const highlightMediaSelection = useHighlightNotifications();

  const requestingHighlight = highlightMediaSelection.requestingHighlight;

  const usableSources: MeetingSourceExtended[] = useMemo(() => {
    if (!isSpectator(session?.role)) return (sources ?? []) as any;
    if (session?.highlighting === MeetingSessionHighlightingState.ONGOING) return (sources ?? []) as any;
    if (session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) return (sources ?? []) as any;
    if (requestingHighlight) return (sources ?? []) as any;
    return [];
  }, [sources, session?.role, session?.highlighting, requestingHighlight]);

  const boardLocalOrRemoteMedia = useBoardMedia();

  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!meeting?.id) return { enabledMedia: [] };
    const studentMeetingOptions = meetingOptions[meeting.id];

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));

    return { enabledMedia };
  }, [meeting?.id, boardLocalOrRemoteMedia, meetingOptions]);

  useMeetingMasterMessagingBridge();

  useMeetingDataOnChange(
    containerAPI,
    (state) => state?.session?.permissions?.audio ?? true,
    (value, previous) => {
      if (value) toastSuccess("Vous pouvez à nouveau activer votre micro.");
      else toastError("L'activation du micro est désormais bloquée.");
    }
  );

  useEffect(() => {
    const emissionManager = containerAPI?.emissionManager;
    if (emissionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (emissionManager.paused) emissionManager.restore();
      } else {
        if (!emissionManager.paused) emissionManager.pause();
      }
    }
    const receptionManager = containerAPI?.receptionManager;
    if (receptionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (receptionManager.paused) receptionManager.restore();
      } else {
        if (!receptionManager.paused) receptionManager.pause();
      }
    }
  }, [containerAPI, meeting?.state])

  ////////////////////// Work Boxes DATA //////////////////////

  const audioMatchRoleComparator = useMemo(() => ({ "session.role": (a: any, b: any) => -compareMeetingSessionRoles(a, b) }), []);

  const canHearSession = useCallback((_: MeetingContainerAPI, receiverSession: MeetingSessionOvenMediaReceiver) => {
    const freshSession = sessions?.find(s => s.id === receiverSession.data.session.id);
    const freshMedia = freshSession?.medias.find(m => m.id === receiverSession.data.mediaSession.id);
    if (!freshSession || !freshMedia) return false;
    const highlighted = populated.activity?.mode === "highlight"
      && populated.activity.confirmedSessions.some(cs => cs.session.id === freshSession.id);
    return matchEngine(
      { session: freshSession, media: freshMedia, currentSession: session, highlighted: !!highlighted },
      features.audioMatch || DEFAULT_AUDIO_MATCH,
      audioMatchRoleComparator,
    );
  }, [features.audioMatch, audioMatchRoleComparator, populated.activity, session, sessions]);

  const audioMixerOptions: AudioMixerOptions = useMemo(() => ({
    ...(settings.debug?.audio?.mixer ?? {}),
    forceEnableDecoderHack: true,
  }), [settings.debug?.audio?.mixer]);

  const audioMixer = useMeetingAudioMixer(
    getAudioContextForAudioMixer(),
    containerAPI,
    true,
    canHearSession,
    audioMixerOptions
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const updateMedias = useCallback(async () => {
    const meetingContainerAPI = containerAPIRef.current;
    if (!meetingContainerAPI) return;

    const manager = meetingContainerAPI.emissionManager;

    if (session) {
      session.medias?.forEach((m) => {
        if (!m.video.enabled && manager.videoIsEnabled({ mediaSessionId: m.id })) {
          manager.disableVideo({ mediaSessionId: m.id });
        } else if (m.video.enabled && !manager.videoIsEnabled({ mediaSessionId: m.id })) {
          manager.enableVideo({ mediaSessionId: m.id });
        }
        if (!m.audio.enabled && manager.audioIsEnabled({ mediaSessionId: m.id })) {
          manager.disableAudio({ mediaSessionId: m.id });
        } else if (m.audio.enabled && !manager.audioIsEnabled({ mediaSessionId: m.id })) {
          manager.enableAudio({ mediaSessionId: m.id });
        }
      });
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }

    dispatch("setAudioStreamEnabled", manager.audioIsEnabled({ mode: "some" }));
    dispatch("setVideoStreamEnabled", manager.videoIsEnabled({ mode: "some" }));
  }, [dispatch, session, meeting]);

  // ------------------ EXPERT UTILS ------------------ //

  const updateMeetingState = useCallback(async (state: MeetingState) => {
    pageContextRef.current?.setLoading(true);
    let result: MeetingUpdateByIdResult | undefined;
    try {
      result = await containerAPIRef.current?.updateMeetingState(state);
    } catch (err) {
    } finally {
      pageContextRef.current?.setLoading(false);
    }
    return result;
  }, []);

  const start = async (): Promise<boolean> => {
    isStoppingRef.current = true;
    pageContextRef.current?.setLoading(true);
    const result = await awaitWithMinimumDelay(updateMeetingState(MeetingState.RUNNING), 500);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.RUNNING) {
      return true;
    }
    isStoppingRef.current = false;
    toastError("Une erreur est apparue.");
    return false;
  };

  const onPause = useCallback(async () => {
    updateMeetingState(MeetingState.PAUSED);
  }, []);

  const onStop = useCallback(async () => {
    setShowEndMeetingConfirmationPopup(true);
  }, []);

  const onResume = useCallback(async () => {
    updateMeetingState(MeetingState.RUNNING);
  }, []);

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!meeting) return;

    if (meeting.specializedData?.isPedagogy) {
      if (pageContextRef.current?.page === PageMasterMode.MEETING_PEDAGOGY) {
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_PEDAGOGY, { id: pageContextRef.current?.params?.id });
      return;
    }

    if (pageContextRef.current?.page !== PageMasterMode.MEETING_DASHBOARD) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: pageContextRef.current?.params?.id });
    }
  }, [pageContextRef]);

  useEffect(() => {
    if (!mediaSelectorRef.current) return;
    mediaSelectorRef.current.enableAudio(audioEnabled);
    mediaSelectorRef.current.enableVideo(videoEnabled);
  }, [audioEnabled, videoEnabled]);

  useEffect(() => {
    if (outputAudioDeviceInfo && audioMixer?.isRunning()) {
      audioMixer.setAudioOutputDevice(outputAudioDeviceInfo).catch(() => {
        console.error(`audio device (${JSON.stringify(outputAudioDeviceInfo)}) is invalid`);
      });
    }
  }, [audioMixer, outputAudioDeviceInfo]);

  useEffect(() => {
    if (!emissionSessions) return;
    emissionSessions.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      if (group && (!s.data.groups || s.data.groups[0] !== group || s.data.groups.length !== 1)) {
        s.data.groups = [group];
      }
    });
  }, [emissionSessions]);

  useEffect(() => {
    if (!receptionSessions) return;
    receptionSessions.forEach((s) => {
      const isExpertVideo = isExpertVideoSession(meeting, s);
      if (s.data.extra?.video !== isExpertVideo || s.data.extra?.audio !== true) {
        s.data.extra = {
          video: isExpertVideo,
          audio: true,
        };
      }
    });
  }, [receptionSessions, meeting]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    if (settings.debug?.audio?.mixer?.public) (window as any).audioMixer = audioMixer;
    if (settings.debug?.meetingContext?.public) (window as any).meetingContext = containerAPI;
  }, [settings, audioMixer, containerAPI]);

  // ------------------ EXPERT EFFECTS ------------------ //

  useEffect(() => {
    const state = meeting?.state;

    let currentPopup: React.ReactNode;
    const endMeetingPopup = () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }

      const stopped = state === MeetingState.STOPPED;

      let buttons: BoxOverlayProps["buttons"];

      if (stopped) {
        buttons = [
          {
            label: "Revenir à la liste des cours",
            onClick: () => {
              removePopup(currentPopup);
              removePopupByGroup("main");
              pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
            },
            validateAfter: 10,
          }
        ];
        if (isExpert(session)) {
          buttons.push({
            label: "Relancer le cours",
            onClick: async () => {
              await start();
              removePopup(currentPopup);
            }
          });
        }
      } else {
        buttons = [
          {
            label: "Terminer le cours",
            onClick: async () => {
              await updateMeetingState(MeetingState.STOPPED);
            }
          },
          {
            label: "Poursuivre le cours",
            onClick: () => {
              setShowEndMeetingConfirmationPopup(false);
            }
          }
        ];
      }

      currentPopup = (
        <OverlayContainer
          visible
          style={{
            width: "100vw",
            height: "100vh",
          }}
          overlay={
            <BoxOverlay
              title={stopped ? "Le cours est terminé" : "Voulez-vous vraiment terminer le cours ?"}
              level={stopped ? "success" : "question"}
              buttons={buttons}
            />
          }
        />
      );

      addPopup(currentPopup, "meeting_state_stopped", "main");
    };

    if (state === MeetingState.STOPPED) {
      setShowEndMeetingConfirmationPopup(false);
    }

    if (state === MeetingState.IDLE) {
      start();
    } else if (state === MeetingState.STOPPED || showEndMeetingConfirmationPopup) {
      endMeetingPopup();
    }

    return () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state, showEndMeetingConfirmationPopup]);

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const workBoxMode = useAppStoreSelector(state => state.workBoxMode);

  const popupMode = useAppStoreSelector(state => state.popupMode);
  const sourcePickerOpen = popupMode === "sources";
  const shareOpen = popupMode === "share";

  useEffect(() => {
    if (isMobile) {
      dispatch("setWorkBoxOpen", false);
      const currentLayout = appStoreCtx.getState().layout;
      if (currentLayout === LayoutType.VerticalSquare) {
        dispatch("setLayout", LayoutType.HorizontalSquare);
      }
    }
  }, [isMobile]);

  const highlightRequestsNb = populated.highlighted.requesting.length;

  type MobileWorkBoxMode = "inputs" | "survey" | "highlight" | "chat";
  const mobileWorkBoxTabs = useMemo(() => {
    const iconStyle = { width: 18, height: 18 };
    const tabs: { mode: MobileWorkBoxMode; icon: React.ReactNode; notificationCount?: number }[] = [
      { mode: "inputs", icon: <ReactIcons.ComputerScreen style={iconStyle} /> },
    ];
    if (features.survey) tabs.push({ mode: "survey", icon: <ReactIcons.Stats style={iconStyle} /> });
    if (isExpert(session) && features.board) tabs.push({ mode: "highlight", icon: <ReactIcons.Hand2 style={iconStyle} />, notificationCount: highlightRequestsNb || undefined });
    tabs.push({ mode: "chat", icon: <ReactIcons.ChatBubble style={iconStyle} />, notificationCount: unreadMessagesNb || undefined });
    return tabs;
  }, [features.survey, features.board, session, highlightRequestsNb, unreadMessagesNb]);

  const handleMobileTabClick = useCallback((mode: MobileWorkBoxMode) => {
    dispatch("setWorkBoxMode", mode);
  }, [dispatch]);

  const closeWorkBoxOverlay = useCallback(() => {
    dispatch("setWorkBoxOpen", false);
  }, [dispatch]);

  const openSourcesOverlay = useCallback(() => {
    dispatch("setPopupMode", "sources");
  }, [dispatch]);

  const closeSourcesOverlay = useCallback(() => {
    dispatch("setPopupMode", undefined);
  }, [dispatch]);

  const closeShareOverlay = useCallback(() => {
    dispatch("setPopupMode", undefined);
  }, [dispatch]);

  if (!meeting || !session || !rooms || !selfUser)  return <LoadingView fullscreen message="Chargement en cours" />;

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderDevSoundIndicator = () => (
    <div style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
      <DevSoundIndicatorsBanner
        meetingData={populated}
        canHearSession={(session) => canHearSession(containerAPI, session)}
      />
    </div>
  );

  const renderMobileWorkBoxOverlay = () => {
    if (!isMobile) return null;
    return (
      <OverlayContainer
        visible={workBoxOpen}
        onClose={closeWorkBoxOverlay}
        overlayStyle={{ padding: "1em", gap: "0.6em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <>
            <div className={styles.mobileWorkBoxTabs} onClick={(e) => e.stopPropagation()}>
              {mobileWorkBoxTabs.map((tab) => (
                <PrimaryButton
                  key={tab.mode}
                  className={clsx(styles.mobileWorkBoxTab, {
                    [styles.mobileWorkBoxTabActive]: workBoxMode === tab.mode,
                  })}
                  onClick={() => handleMobileTabClick(tab.mode)}
                >
                  {tab.notificationCount !== undefined && tab.notificationCount > 0 && workBoxMode !== tab.mode && (
                    <div className={styles.mobileWorkBoxTabBadge}>{tab.notificationCount}</div>
                  )}
                  {tab.icon}
                </PrimaryButton>
              ))}
            </div>
            <div
              className={styles.mobileWorkBoxOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <CurrentWorkBox chatInfo={chatInfo} onClose={closeWorkBoxOverlay} />
            </div>
          </>
        }
      />
    );
  };

  const renderAlwaysMountedSourcesOverlay = () => {
    const highlightMode = highlightMediaSelection.mode;
    const isHighlightMode = highlightMode !== "none";
    const isVisible = !!sourcePickerOpen || isHighlightMode;

    // Requesting: clicking outside cancels the request
    // Confirming: cannot close by clicking outside
    // Normal: close the source picker
    const handleClose = highlightMode === "requesting"
      ? highlightMediaSelection.onCancel
      : highlightMode === "confirming"
      ? undefined
      : closeSourcesOverlay;

    const highlightFooter = isHighlightMode ? (
      <div className={styles.highlightFooter}>
        {highlightMode === "confirming" && highlightMediaSelection.remainingSeconds !== undefined && highlightMediaSelection.remainingSeconds > 0 && (
          <span className={styles.highlightTimer}>
            Confirmation automatique dans {highlightMediaSelection.remainingSeconds}s
          </span>
        )}
        <div className={styles.highlightActions}>
          <Button.Simple
            label={highlightMode === "requesting" ? "Annuler" : "Refuser"}
            onClick={highlightMediaSelection.onCancel}
            className={clsx(styles.highlightPopupBtn, styles.cancel)}
          />
          <Button.Simple
            label={highlightMode === "requesting" ? "Confirmer et demander" : "Confirmer et intervenir"}
            onClick={highlightMediaSelection.onConfirm}
            className={styles.highlightPopupBtn}
          />
        </div>
      </div>
    ) : undefined;

    const title = highlightMode === "requesting"
      ? "Montée au tableau — Configurez vos sources"
      : highlightMode === "confirming"
      ? "Intervention — Définissez vos sources"
      : undefined;

    return (
      <OverlayContainer
        visible={isVisible}
        unmountWhenInvisible={false}
        onClose={handleClose}
        overlayStyle={{ padding: "1em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <div
            className={styles.sourcesOverlay}
            onClick={(e) => e.stopPropagation()}
          >
            <MediaSourceWorkBox
              title={title}
              footer={highlightFooter}
              onClose={handleClose}
            >
              <MediaSourcePicker
                meetingAPI={containerAPI}
                requireBackendMedia={!!session?.medias?.length}
                sources={usableSources}
                display
                style={{ width: "100%", height: "100%" }}
              />
            </MediaSourceWorkBox>
          </div>
        }
      />
    );
  };

  const renderShareOverlay = () => {
    return (
      <OverlayContainer
        visible={shareOpen}
        onClose={closeShareOverlay}
        overlayStyle={{ padding: "1em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <div
            className={styles.shareOverlay}
            onClick={(e) => e.stopPropagation()}
          >
            <ShareWorkBox onClose={closeShareOverlay} />
          </div>
        }
      />
    );
  };

  const renderUI = (children: React.ReactNode) => {
    if (isMobile) {
      return (
        <div className={clsx(styles.uiContainer, styles.uiContainerMobile)}>
          <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}>
            <SurveyBar />
            <div style={{
              height: "100%",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              padding: "0.5em",
              gap: "0.5em",
            }}>
              <div style={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                position: "relative",
              }}>
                {children}
                {renderDevSoundIndicator()}
              </div>
              <BottomBar
                onUpdateMeetingState={updateMeetingState}
                onPause={onPause}
                onStop={onStop}
                sources={usableSources}
                showLayoutSelector={enabledMedia.length > 1}
                onOpenSources={openSourcesOverlay}
                sourcesActive={sourcePickerOpen}
                notificationCount={(unreadMessagesNb || 0) + (populated.highlighted.requesting.length || 0) || undefined}
                onRequestHighlight={highlightMediaSelection.startRequest}
              />
            </div>
          </div>
          {renderMobileWorkBoxOverlay()}
        </div>
      );
    }

    return (
      <div className={clsx({
        [styles.uiContainer]: true,
        [styles.boxOpen]: workBoxOpen,
      })}>
        <SideBar
          chatAdapter={chatInfo?.adapter}
          highlightRequestsNb={populated.highlighted.requesting.length}
        />
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}>
          <SurveyBar />
          <div style={{
            height: "100%",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: "1em 1em 1em 0em",
            gap: "1em",
          }}>
            <div style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
            }}>
              <div className={styles.workContainer}>
                <RevealContainer
                  open={workBoxOpen}
                  gap="1em"
                  duration={500}
                  easing="cubic-bezier(.4, 0, .2, 1)"
                  className={styles.wbRevealContainer}
                >
                  <CurrentWorkBox
                    chatInfo={chatInfo}
                  />
                </RevealContainer>
                <div className={styles.childrenContainer}>
                  {children}
                  {renderDevSoundIndicator()}
                </div>
              </div>
            </div>
            <BottomBar
              onUpdateMeetingState={updateMeetingState}
              onPause={onPause}
              onStop={onStop}
              sources={usableSources}
              showLayoutSelector={enabledMedia.length > 1}
              onRequestHighlight={highlightMediaSelection.startRequest}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderDebugOutgoingStream = () => {
    if (!settings?.debug?.displayOutgoingStreamStats && !settings?.debug?.displaySessionIds) return null;
    return (
      <BottomPanelWebrtcSessionStats
        incomingSessions={settings?.debug?.displayIncomingStreamStats ? receptionSessions : undefined}
        outgoingSessions={settings?.debug?.displayOutgoingStreamStats ? emissionSessions : undefined}
        sessions={(emissionSessions ?? []).map((s, i) => ({
          id: s.getId(),
          name: `${s.data?.mediaSession?.name ?? `ouput n°${i}`}`,
          session: settings?.debug?.displayOutgoingStreamStats ? s : undefined,
          meeting: settings?.debug?.displaySessionIds ? { session: session, mediaSession: s.data?.mediaSession } : undefined,
        }))}
      />
    );
  };

  return (
    <MasterUI>
      <PauseView
        onResume={onResume}
        enabled={isExpert(session)}
        meetingPaused={meeting?.state === MeetingState.PAUSED}
        meetingPausedAt={meeting?.pausedAt}
      >
        <div className={styles.mainContainer}>
          {renderUI(<Outlet />)}
          {renderDebugOutgoingStream()}
          <HighlightPopup />
          {renderAlwaysMountedSourcesOverlay()}
          {renderShareOverlay()}
        </div>
      </PauseView>
    </MasterUI>
  );
};
